import type {
  ComposerImageAttachment,
  ComposerFileAttachment,
  ComposerThreadDraftState,
  PersistedComposerImageAttachment,
} from "./composerDraftStore";
import type { TerminalContextDraft } from "./lib/terminalContext";

export interface ComposerDraftContentSnapshot {
  readonly prompt: string;
  readonly images: ReadonlyArray<ComposerImageAttachment>;
  readonly files: ReadonlyArray<ComposerFileAttachment>;
  readonly persistedAttachments: ReadonlyArray<PersistedComposerImageAttachment>;
  readonly terminalContexts: ReadonlyArray<TerminalContextDraft>;
}

export function captureComposerDraftContent(
  draft: ComposerThreadDraftState,
): ComposerDraftContentSnapshot {
  return {
    prompt: draft.prompt,
    images: [...draft.images],
    files: [...(draft.files ?? [])],
    persistedAttachments: [...draft.persistedAttachments],
    terminalContexts: [...draft.terminalContexts],
  };
}

function imageData({ file: _file, previewUrl: _previewUrl, ...image }: ComposerImageAttachment) {
  return image;
}

export function composerDraftContentMatches(
  draft: ComposerThreadDraftState,
  snapshot: ComposerDraftContentSnapshot,
): boolean {
  return (
    draft.prompt === snapshot.prompt &&
    JSON.stringify(draft.images.map(imageData)) ===
      JSON.stringify(snapshot.images.map(imageData)) &&
    JSON.stringify((draft.files ?? []).map(({ file: _file, ...attachment }) => attachment)) ===
      JSON.stringify(snapshot.files.map(({ file: _file, ...attachment }) => attachment)) &&
    JSON.stringify(draft.persistedAttachments) === JSON.stringify(snapshot.persistedAttachments) &&
    JSON.stringify(draft.terminalContexts) === JSON.stringify(snapshot.terminalContexts)
  );
}

export function clearComposerDraftContent(
  draft: ComposerThreadDraftState,
): ComposerThreadDraftState {
  const { lastGeneratedPullRequestPrompt: _sentHandoff, ...withoutHandoff } = draft;
  return {
    ...withoutHandoff,
    prompt: "",
    images: [],
    files: [],
    nonPersistedImageIds: [],
    persistedAttachments: [],
    terminalContexts: [],
    stashTerminalContexts: [],
  };
}

export function shouldRemoveComposerDraft(draft: ComposerThreadDraftState): boolean {
  return (
    draft.prompt.length === 0 &&
    draft.lastGeneratedPullRequestPrompt === undefined &&
    draft.images.length === 0 &&
    (draft.files?.length ?? 0) === 0 &&
    draft.persistedAttachments.length === 0 &&
    draft.terminalContexts.length === 0 &&
    Object.keys(draft.modelSelectionByProvider).length === 0 &&
    draft.activeProvider === null &&
    draft.runtimeMode === null &&
    draft.interactionMode === null &&
    draft.stagedSubmission == null
  );
}
